// PreventiApp · AuthContext
// Gestiona sesión persistente y detecta el flag requiere_cambio_password
// para forzar el flujo de primer ingreso.
//
// Sprint 5 · Paso 2 · Al signOut se limpia el cache de TanStack Query para
// que un login posterior con OTRA cuenta NO vea datos residuales de la anterior.

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { setActor as setMetricsActor, clearActor as clearMetricsActor } from '../lib/metrics';

const AuthContext = createContext({
  session: null,
  profile: null,
  needsPasswordChange: false,
  loading: true,
  refreshProfile: async () => {},
  signOut: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const lastUserIdRef = useRef(null);

  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      setNeedsPasswordChange(false);
      return;
    }
    // Cambio de usuario: purgar cache TanStack para no cruzar datos.
    if (lastUserIdRef.current && lastUserIdRef.current !== userId) {
      queryClient.clear();
    }
    lastUserIdRef.current = userId;
    const { data, error } = await supabase
      .from('profiles')
      .select('id, rol, colegio_id, nombre_completo, correo, requiere_cambio_password, estado, es_inside_staff, permisos_inside')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('[Auth] Error cargando profile:', error.message);
    }
    setProfile(data || null);
    setNeedsPasswordChange(!!data?.requiere_cambio_password);
    if (data) {
      // Sprint 5 · métricas mínimas: registra rol y colegio HASHEADOS.
      setMetricsActor({ rol: data.rol, colegio_id: data.colegio_id });
    }
  }, [queryClient]);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      if (s?.user) await loadProfile(s.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, s) => {
      if (!mounted) return;
      setSession(s);
      if (s?.user) await loadProfile(s.user.id);
      else { setProfile(null); setNeedsPasswordChange(false); }
      setLoading(false);
    });

    return () => { mounted = false; subscription?.unsubscribe(); };
  }, [loadProfile]);

  const refreshProfile = useCallback(async () => {
    if (session?.user) await loadProfile(session.user.id);
  }, [session, loadProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setNeedsPasswordChange(false);
    // Sprint 5 · Paso 2 · limpia todo el cache de queries al cerrar sesión.
    queryClient.clear();
    lastUserIdRef.current = null;
    // Sprint 5 · métricas: purga los hashes de rol/colegio de la sesión.
    clearMetricsActor();
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ session, profile, needsPasswordChange, loading, refreshProfile, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
